/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormBuilder} from '@angular/forms';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {InputDropdownBase} from '../input-dropdown-base/input-dropdown-base';
import {InputMultiSelectOptionModel} from './input-multi-select-option.model';

@Component({
    selector: 'app-input-multi-select',
    templateUrl: './input-multi-select.component.html',
    styleUrls: ['../input/input.component.css', './input-multi-select.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
// Class must implement lifecycle hooks because it is metadata for Angular
export class InputMultiSelectComponent<T> extends InputDropdownBase implements OnInit, OnDestroy {

    /**
     * The dropdown options array
     */
    @Input() options: InputMultiSelectOptionModel<T>[] = [];
    /**
     * The dropdown template variable
     */
    @ViewChild('dropdown') dropdown: NgbDropdown;
    /**
     * Array for the selected options
     */
    private selectedOptions: InputMultiSelectOptionModel<T>[] = [];

    get optionDataCy(): string {
        return `${this.baseCypressTag}-option`;
    }

    constructor(protected formBuilder: UntypedFormBuilder) {
        super(formBuilder);
    }

    /**
     * Returns the input text string value according to the number of selected options from the dropdown
     */
    get inputTextString(): string {
        let value = null;
        if (this.selectedOptions != null) {
            if (this.selectedOptions.length === 1) {
                value = this.selectedOptions[0].label;
            } else if (this.selectedOptions.length > 1) {
                value = `Selected (${this.selectedOptions.length})`;
            }
        }
        return value;
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    /**
     * For a given option, return its unique id for the label and checkbox input, based on its label value
     */
    getOptionId(option: InputMultiSelectOptionModel<T>): string {
        if (option != null && option.label != null) {
            // Trim spaces and replace dashes with spaces to avoid double dashes in id
            const cleanLabel = option.label.trim().replace(/-/g, ' ');
            return cleanLabel.replace(/\s+/g, '-').toLowerCase();
        }
    }

    /**
     * Reacts after the change of an option checked status, updating the selected options array and the input value
     */
    optionCheckedStatusChange(option: InputMultiSelectOptionModel<T>): void {
        if (this.optionIsChecked(option)) {
            this.selectedOptions = this.selectedOptions.filter(item => item.label !== option.label);
        } else {
            this.selectedOptions.push(option);
        }

        this.setValue(this.selectedOptions);
    }

    /**
     * Unchecks all options and sets selectedOptions as empty array when the value on the input is deleted
     */
    deleteInputValue(): void {
        super.deleteInputValue();
        this.selectedOptions = [];
    }

    /**
     * Opens and closes the dropdown on the arrow click
     */
    clickOnArrow(event: any): void {
        this.inputFocus.emit(event);
        if (this.dropdown.isOpen()) {
            this.dropdown.close();
        } else {
            this.dropdown.open();
        }
    }

    setValue(value: InputMultiSelectOptionModel<T>[], options?: { emitEvent: boolean }): void {
        super.setValue(value, options);
        this.selectedOptions = value;
    }

    /**
     * Returns if a given option should be checked
     */
    optionIsChecked(option: InputMultiSelectOptionModel<T>): boolean {
        return this.selectedOptions.find(item => item.label === option.label) != null;
    }
}
